import { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Phone, MessageCircle, Mail, Sparkles } from 'lucide-react';

function LeadRow({ lead, onOpenLead }) {
  const waDigits = (lead.mobile || lead.phone || '').replace(/[^0-9]/g, '');
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`my-day-lead-${lead.id}`}>
      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpenLead(lead.id)}>
        <div className="font-medium truncate">{lead.company}</div>
        <div className="text-xs text-muted-foreground truncate">{lead.contact_name} · {lead.country} · {lead.code}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        {lead.phone && <a href={`tel:${lead.phone}`} data-testid={`my-day-call-${lead.id}`}><Button size="icon" variant="outline" className="h-8 w-8"><Phone className="h-3.5 w-3.5" /></Button></a>}
        {waDigits && <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" data-testid={`my-day-whatsapp-${lead.id}`}><Button size="icon" variant="outline" className="h-8 w-8"><MessageCircle className="h-3.5 w-3.5" /></Button></a>}
        {lead.email && !lead.do_not_email && <a href={`mailto:${lead.email}`} data-testid={`my-day-email-${lead.id}`}><Button size="icon" variant="outline" className="h-8 w-8"><Mail className="h-3.5 w-3.5" /></Button></a>}
      </div>
    </div>
  );
}

function Section({ title, leads, onOpenLead, testId, emptyText }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="font-semibold mb-1 flex items-center gap-2">{title} <Badge variant="secondary">{leads.length}</Badge></h3>
      {leads.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3">{emptyText}</p>
      ) : (
        <div>{leads.map(l => <LeadRow key={l.id} lead={l} onOpenLead={onOpenLead} />)}</div>
      )}
    </Card>
  );
}

export default function MyDayTab({ onOpenLead }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await leadsAPI.myDay();
      setData(data);
    } catch (e) {
      toast.error('Failed to load My Day');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground p-4">Loading...</div>;

  return (
    <div className="space-y-4 mt-4" data-testid="my-day-content">
      {data.unreachable_prompts.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50" data-testid="my-day-unreachable">
          <h3 className="font-semibold mb-1 flex items-center gap-2 text-amber-800"><Sparkles className="h-4 w-4" /> Consider closing (unreachable) <Badge variant="secondary">{data.unreachable_prompts.length}</Badge></h3>
          {data.unreachable_prompts.map(l => <LeadRow key={l.id} lead={l} onOpenLead={onOpenLead} />)}
        </Card>
      )}
      <Section title="Overdue" leads={data.overdue} onOpenLead={onOpenLead} testId="my-day-overdue" emptyText="Nothing overdue" />
      <Section title="Due today" leads={data.today} onOpenLead={onOpenLead} testId="my-day-today" emptyText="Nothing due today" />
      <Section title="New leads" leads={data.new} onOpenLead={onOpenLead} testId="my-day-new" emptyText="No new leads" />
    </div>
  );
}
